import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Answer,
  Difficulty,
  Question,
} from "../../../../../../domain/model/Question";
import { Category } from "../../../../../../domain/model/Category";
import {
  Attachment,
  AttachmentType,
} from "../../../../../../domain/model/Attachment";
import { GetQuestionUseCase } from "../../../../../../domain/usecase/question/get/GetUseCase";
import { State } from "../../../../../../domain/model/ResponseState";
import { GetAllCategoriesUseCase } from "../../../../../../domain/usecase/category/getAll/GetAllUseCase";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../../../../data/constant/RouteConstants";
import { GetAnswerByQuestionIdUseCase } from "../../../../../../domain/usecase/answer/getByQuestionId/GetByQuestionIdUseCase";
import { CreateQuestionUseCase } from "../../../../../../domain/usecase/question/create/CreateUseCase";
import { UpdateQuestionUseCase } from "../../../../../../domain/usecase/question/update/UpdateUseCase";
import { DeleteQuestionUseCase } from "../../../../../../domain/usecase/question/delete/DeleteUseCase";
import { CreateAnswerUseCase } from "../../../../../../domain/usecase/answer/create/CreateUseCase";
import { UpdateAnswerUseCase } from "../../../../../../domain/usecase/answer/update/UpdateUseCase";
import { DeleteAnswerUseCase } from "../../../../../../domain/usecase/answer/delete/DeleteUseCase";
import { UploadFileUseCase } from "../../../../../../domain/usecase/uploadFile/UploadFileUseCase";
import { GetAllQuestionsUseCase } from "../../../../../../domain/usecase/question/getAll/GetAllUseCase";
import SharedPreferences from "../../../../../../core/utils/SharedPreferences";
import { STORAGE_KEYS } from "../../../../../../data/constant/Constants";
import { DeleteQuestionAttachmentUseCase } from "../../../../../../domain/usecase/question/deleteAttachment/UseCase";
import { DeleteAnswerAttachmentUseCase } from "../../../../../../domain/usecase/answer/deleteAttachment/UseCase";
import { showGlobalLoadingView } from "../../../../../../core/utils/Utils";

export function QuestionDetailsViewModel(
  getQuestionUseCase: GetQuestionUseCase,
  getAllQuestionsUseCase: GetAllQuestionsUseCase,
  getAllCategoriesUseCase: GetAllCategoriesUseCase,
  getAnswerByQuestionIdUseCase: GetAnswerByQuestionIdUseCase,
  createQuestionUseCase: CreateQuestionUseCase,
  updateQuestionUseCase: UpdateQuestionUseCase,
  deleteQuestionUseCase: DeleteQuestionUseCase,
  deleteQuestionAttachmentUseCase: DeleteQuestionAttachmentUseCase,
  createAnswerUseCase: CreateAnswerUseCase,
  updateAnswerUseCase: UpdateAnswerUseCase,
  deleteAnswerUseCase: DeleteAnswerUseCase,
  deleteAnswerAttachmentUseCase: DeleteAnswerAttachmentUseCase,
  uploadFileUseCase: UploadFileUseCase,
) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const [backdropLoadingStatus, setBackdropLoadingStatus] = useState("");
  const [isBackdropLoading, setIsBackdropLoading] = useState(false);
  const [attachmentBeingUsedFor, setAttachmentBeingUsedFor] = useState<
    "question" | "answer"
  >("question");
  const [question, setQuestion] = useState<Question | undefined>({
    title: "",
    difficulty: Difficulty.EASY,
  });

  const [answer, setAnswer] = useState<Answer | undefined>({
    title: "",
  });

  const [categories, setCategories] = useState<Array<Category> | undefined>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >();

  const [toDeleteQuestionAttachment, setToDeleteQuestionAttachment] =
    useState(false);

  useEffect(() => {
    setQuestion({
      ...question,
      category: {
        _id: selectedCategoryId,
        title: categories?.filter((c) => c._id === selectedCategoryId)[0].title,
      },
    });
  }, [selectedCategoryId]);

  useEffect(() => {
    if (categories) fetchLastQuestion();
  }, [categories]);

  async function fetchQuestion(id: string) {
    setLoading(true);
    const response = await getQuestionUseCase.invoke(id);
    switch (response.responseState) {
      case State.Success:
        if (!response.data?._id) return;
        const categories = await fetchCategories();
        if (!categories) {
          return;
        }
        setQuestion(response.data);
        if (response.data?.category?._id) {
          setSelectedCategoryId(response.data?.category._id);
        }
        await fetchAnswer(response.data?._id);

        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        setLoading(false);
        break;
    }
  }

  async function fetchLastQuestion() {
    const response = await getAllQuestionsUseCase.invoke(0, 1);
    switch (response.responseState) {
      case State.Success:
        if (question?._id) return;
        if (response.data && response.data.length === 1) {
          const qCategory = response.data[0].category;

          if (
            categories?.length != 0 &&
            categories?.filter((c) => c._id == qCategory?._id).length == 0
          ) {
            setSelectedCategoryId(categories[0]._id);
            setQuestion({
              ...question,
              title: response.data![0].title,
              category: {
                _id: categories[0]._id,
                title: categories[0].title,
              },
              difficulty: response.data[0].difficulty,
            });
          } else {
            setSelectedCategoryId(qCategory?._id);
            setQuestion({
              ...question,
              title: response.data![0].title,
              category: {
                _id: response.data[0].category?._id,
                title: qCategory?.title,
              },
              difficulty: response.data[0].difficulty,
            });
          }
        } else {
          if (!categories) return;
          if (categories.length == 0) {
            showSnackbar("There is no category yet. Create one.");
            navigate(ROUTE_ENDPOINTS.HOME_CATEGORIES);
            return;
          }

          if (!categories[0]._id) return;
          setSelectedCategoryId(categories[0]._id);

          const subCs = getCategoryById(
            question?.category?._id ?? categories[0]._id,
          );
        }
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchCategories(): Promise<Array<Category> | undefined> {
    setLoading(true);
    const response = await getAllCategoriesUseCase.invoke();
    setLoading(false);
    switch (response.responseState) {
      case State.Success:
        setCategories(response.data);
        return response.data;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchAnswer(qid: string) {
    setLoading(true);
    const response = await getAnswerByQuestionIdUseCase.invoke(qid);
    setLoading(false);
    switch (response.responseState) {
      case State.Success:
        setAnswer(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function deleteQuestion() {
    if (!question?._id) return;

    setBackdropLoadingStatus("Deleting question...");
    setIsBackdropLoading(true);
    const response = await deleteQuestionUseCase.invoke(question?._id);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        await deleteAnswer();
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function saveQuestion() {
    if (!question || !answer) return;

    if (!question.title) {
      showSnackbar("Question cannot be empty");
      return;
    }

    if (!answer.title) {
      showSnackbar("Answer cannot be empty");
      return;
    }

    setIsBackdropLoading(true);
    if (question.attachment?.file) {
      const attachment = await uploadFile(question.attachment.file);
      if (!attachment) {
        setIsBackdropLoading(false);
        return;
      }
      question.attachment = attachment;
    }

    if (answer.attachment?.file) {
      const attachment = await uploadFile(answer.attachment.file);
      if (!attachment) {
        setIsBackdropLoading(false);
        return;
      }
      answer.attachment = attachment;
    }

    const newQuestion: any = {
      _id: question._id,
      title: question.title,
      attachment: question.attachment?._id,
      difficulty: question.difficulty,
      category: selectedCategoryId,
    };

    setBackdropLoadingStatus("Saving question...");
    setIsBackdropLoading(true);
    const response = await updateQuestionUseCase.invoke(newQuestion);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        await saveAnswer();
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function createQuestion() {
    if (!question || !answer) return;

    if (!question.title) {
      showSnackbar("Question cannot be empty");
      return;
    }

    if (!question.category) {
      showSnackbar("Question category cannot be empty");
      return;
    }

    if (!answer.title) {
      showSnackbar("Answer cannot be empty");
      return;
    }

    setBackdropLoadingStatus("Creating question...");
    setIsBackdropLoading(true);
    if (question.attachment?.file) {
      const attachment = await uploadFile(question.attachment.file);
      if (!attachment) {
        setIsBackdropLoading(false);
        return;
      }
      question.attachment = attachment;
    }

    if (answer.attachment?.file) {
      const attachment = await uploadFile(answer.attachment.file);
      if (!attachment) {
        setIsBackdropLoading(false);
        return;
      }
      answer.attachment = attachment;
    }

    const newQuestion: Question | any = {
      title: question.title,
      attachment: question.attachment?._id,
      difficulty: question.difficulty,
      category: selectedCategoryId,
    };

    const response = await createQuestionUseCase.invoke(newQuestion);
    switch (response.responseState) {
      case State.Success:
        const questionId = response.data?.id;
        if (questionId) await createAnswer(questionId);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function deleteAnswer() {
    if (!answer?._id) return;

    setBackdropLoadingStatus("Deleting answer...");
    setIsBackdropLoading(true);
    const response = await deleteAnswerUseCase.invoke(answer?._id);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_QUESTIONS);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function deleteQuestionOrAnswerAttachment() {
    if (!question?._id) return;
    if (!answer?._id) return;

    showGlobalLoadingView(true);
    const response = toDeleteQuestionAttachment
      ? await deleteQuestionAttachmentUseCase.invoke(question?._id)
      : await deleteAnswerAttachmentUseCase.invoke(answer?._id);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        if (toDeleteQuestionAttachment) {
          setQuestion({ ...question, attachment: undefined });
        } else {
          setAnswer({ ...answer, attachment: undefined });
        }
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function saveAnswer() {
    if (!answer) return;

    const newAnswer: Answer | {} = {
      _id: answer._id,
      title: answer.title,
      attachment: answer.attachment?._id,
      question: question?._id,
    };

    setBackdropLoadingStatus("Saving answer...");
    setIsBackdropLoading(true);
    const response = await updateAnswerUseCase.invoke(newAnswer);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_QUESTIONS);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function createAnswer(qid: string) {
    if (!answer) return;

    const newAnswer: Answer | {} = {
      title: answer.title,
      attachment: answer.attachment?._id,
      question: qid,
    };

    setBackdropLoadingStatus("Creating answer...");
    setIsBackdropLoading(true);
    const response = await createAnswerUseCase.invoke(newAnswer);
    switch (response.responseState) {
      case State.Success:
        navigate(ROUTE_ENDPOINTS.HOME_QUESTIONS);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function uploadFile(file: File): Promise<Attachment | undefined> {
    const formData = new FormData();
    formData.append("file", file);

    showGlobalLoadingView(true);
    const response = await uploadFileUseCase.invoke(formData);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        return response.data;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function getCategoryById(id: string): Category | undefined {
    if (!categories) return;
    return categories.filter((category) => category._id === id)[0];
  }

  function getCategoryByTitle(title: string): Category | undefined {
    if (!categories) return;
    return categories.filter((category) => category.title === title)[0];
  }

  function setImageUrl(file: File) {
    if (!file) return;
  }

  function setTitleForQuestion(title: string) {
    setQuestion({
      ...question,
      title: title,
    });
  }

  function setTitleForAnswer(title: string) {
    setAnswer({
      ...answer,
      title: title,
    });
  }

  function setDifficulty(difficulty: string) {
    setQuestion({
      ...question,
      difficulty: difficulty,
    });
  }

  function setQuestionOrAnswerAttachment(
    attachmentFile: File,
    attachmentType: AttachmentType,
  ) {
    if (attachmentBeingUsedFor == "question")
      setQuestion({
        ...question,
        attachment: { file: attachmentFile, type: attachmentType },
      });
    else
      setAnswer({
        ...answer,
        attachment: { file: attachmentFile, type: attachmentType },
      });
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error",
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    isLoading,
    question,
    answer,
    categories,
    setDifficulty,
    fetchCategories,
    setAttachmentBeingUsedFor,
    fetchQuestion,
    setQuestionOrAnswerAttachment,
    getCategoryByTitle,
    selectedCategoryId,
    setSelectedCategoryId,
    getCategoryById,
    backdropLoadingStatus,
    setImageUrl,
    setTitleForQuestion,
    isBackdropLoading,
    setTitleForAnswer,
    createQuestion,
    deleteQuestion,
    saveQuestion,
    deleteQuestionOrAnswerAttachment,
    setToDeleteQuestionAttachment,
  };
}
